import React, { useEffect, useState } from "react";
import { Formik, Form, Field } from "formik";
import * as yup from "yup";
import { toast } from "react-toastify";
import Modal from "./Modal";
import { createTeacher } from "../../utils/createTeacher";

interface SchoolOption {
  id: string;
  name: string;
  district_id: string;
}

interface AddTeacherModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  onSuccess?: () => void;
  schoolId?: string;
  schools?: SchoolOption[];
  districtId?: string;
}

const validationSchema = yup.object().shape({
  email: yup.string().email("Invalid email").required("Email is required"),
  password: yup
    .string()
    .min(6, "Password must be at least 6 characters")
    .required("Password is required"),
  firstName: yup.string().required("First name is required"),
  lastName: yup.string().required("Last name is required"),
  schoolId: yup.string().when("schools", {
    is: (schools: any) => Array.isArray(schools) && schools.length > 0,
    then: (schema) => schema.required("School is required"),
    otherwise: (schema) => schema,
  }),
});

export default function AddTeacherModal({
  isOpen,
  onClose,
  title,
  onSuccess,
  schoolId,
  schools = [],
  districtId,
}: AddTeacherModalProps) {
  const initialSchoolId = schoolId
    ? String(schoolId)
    : schools.length > 0
    ? String(schools[0].id)
    : "";

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={title || "Add New Teacher"}>
      <Formik
        initialValues={{
          email: "",
          password: "",
          firstName: "",
          lastName: "",
          schoolId: initialSchoolId,
        }}
        validationSchema={validationSchema}
        onSubmit={async (values, { setSubmitting, resetForm }) => {
          const fullName = `${values.firstName} ${values.lastName}`.trim();
          const usedSchoolId = schoolId || values.schoolId;
          if (!usedSchoolId) {
            toast.error("School is required");
            setSubmitting(false);
            return;
          }

          // Determine the correct district_id
          let usedDistrictId = districtId || "";
          if (schools.length > 0 && values.schoolId) {
            // If schools array is provided and a school is selected, get the district from the selected school
            const selectedSchool = schools.find(
              (s) => String(s.id) === String(values.schoolId)
            );
            if (selectedSchool) {
              usedDistrictId = selectedSchool.district_id;
            }
          }

          const { error } = await createTeacher({
            email: values.email,
            password: values.password,
            firstName: values.firstName,
            lastName: values.lastName,
            fullName,
            school_id: usedSchoolId,
            district_id: usedDistrictId,
          });
          if (error) {
            toast.error(error);
            setSubmitting(false);
            return;
          }
          toast.success("Teacher successfully added!");
          resetForm();
          onClose();
          onSuccess?.();
        }}
        enableReinitialize
      >
        {(formik) => (
          <Form className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Email
              </label>
              <Field
                name="email"
                className="mt-1 block w-full h-10 border rounded-md border-gray-300 p-2 focus:border-indigo-500 focus:ring-indigo-500"
              />
              {formik.errors.email && formik.touched.email && (
                <div className="text-red-500 text-sm">
                  {formik.errors.email}
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <Field
                name="password"
                type="password"
                className="mt-1 block w-full h-10 border rounded-md border-gray-300 p-2 focus:border-indigo-500 focus:ring-indigo-500"
              />
              {formik.errors.password && formik.touched.password && (
                <div className="text-red-500 text-sm">
                  {formik.errors.password}
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                First Name
              </label>
              <Field
                name="firstName"
                className="mt-1 block w-full h-10 border rounded-md border-gray-300 p-2 focus:border-indigo-500 focus:ring-indigo-500"
              />
              {formik.errors.firstName && formik.touched.firstName && (
                <div className="text-red-500 text-sm">
                  {formik.errors.firstName}
                </div>
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Last Name
              </label>
              <Field
                name="lastName"
                className="mt-1 block w-full h-10 border rounded-md border-gray-300 p-2 focus:border-indigo-500 focus:ring-indigo-500"
              />
              {formik.errors.lastName && formik.touched.lastName && (
                <div className="text-red-500 text-sm">
                  {formik.errors.lastName}
                </div>
              )}
            </div>
            {schools.length > 0 && !schoolId && (
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  School
                </label>
                <Field
                  as="select"
                  name="schoolId"
                  className="mt-1 block w-full h-10 border rounded-md border-gray-300 p-2 focus:border-indigo-500 focus:ring-indigo-500"
                >
                  {schools.map((s) => (
                    <option key={s.id} value={String(s.id)}>
                      {s.name}
                    </option>
                  ))}
                </Field>
                {formik.errors.schoolId && formik.touched.schoolId && (
                  <div className="text-red-500 text-sm mt-1">
                    {formik.errors.schoolId}
                  </div>
                )}
              </div>
            )}
            <div className="pt-4 flex justify-end space-x-2">
              <button
                type="button"
                onClick={onClose}
                className="btn btn-small btn-secondary"
                disabled={formik.isSubmitting}
              >
                Cancel
              </button>
              <button
                type="submit"
                className={`btn btn-small ${
                  formik.isSubmitting ? "btn-disabled" : ""
                }`}
                disabled={formik.isSubmitting || !formik.isValid}
              >
                {formik.isSubmitting ? "Adding..." : "Add Teacher"}
              </button>
            </div>
          </Form>
        )}
      </Formik>
    </Modal>
  );
}
